import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STORAGE_UNITS } from '../../specimens/StoragePanel';
import { localToday, storageErrorMessage } from '../../SlantStoragePanel';
import {
  cutSetTubes, defaultPlateId, depositBody, depositProblem, depositSources, duplicateWell, nextWells,
} from '../quickActions';
import { Code } from '../cells';
import { FormButtons, FormError, PlatePicker, grid, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';

/*
 * Deposit to bank (POST /slants/deposit): an isolate's culture into the bank
 * in one step. The tubes are either the slants it already has that are not
 * stored yet, or a new set of four cut from one of its plates now -- the
 * server cuts SA to SD and places them in the same transaction, so a refusal
 * leaves no set cut and nothing stored.
 *
 * Every tube goes into one place (unit, Freezer ID, rack, box) with one
 * preservation record, each into its own well. The first well fills the
 * rest along the row (nextWells); a tube's own well can then be changed, and
 * changing the first well fills them all again.
 *
 * The rules are the server's (slants.py deposit_slants, and for each tube
 * the rules of PUT /slants/{id}/storage). The form says the ones it can
 * before sending, and turns each refusal's code into a sentence.
 */

/** Refusals SlantStoragePanel already words, one tube at a time: said here
 *  in the same sentences (the slants catalogue's storage.errors.*). */
const STORAGE_REFUSALS = ['preservation_incomplete', 'specimen_code_required', 'invalid_storage', 'invalid_text',
  'invalid_preserved_at', 'unknown_technician'];

/** A deposit's refusal in the reader's words, or null for one with no code
 *  of ours -- the server's own words are shown then. `t` is the hub's,
 *  `tSlants` the slants catalogue's, which storageErrorMessage speaks. */
export function depositRefusal(data, t, tSlants) {
  switch (data?.code) {
    case 'well_occupied':
      return t('quick.problems.wellOccupied', { holder: data.holder, code: data.slant_code });
    case 'duplicate_well':
      return t('quick.problems.duplicateWell', { well: data.well });
    case 'slants_exist':
      // A deleted set keeps its codes: it is restored, never cut again.
      return t(data.deleted ? 'quick.problems.slantsDeleted' : 'quick.problems.slantsExist');
    // The plate stopped growing (or went on) since the form was opened.
    case 'plate_not_depositable':
      return t('quick.problems.plateNotDepositable', {
        code: data.plate_code, status: data.status ? t(`plates:plateStatus.${data.status}`) : '',
      });
    case 'invalid_place':
      return t('quick.problems.unitRequired');
    // A tube that is not this deposit's to place: already in the bank (moved
    // from its own storage panel), discarded or archived, or another isolate's.
    case 'already_banked':
      return t('quick.problems.alreadyBanked', { code: data.slant_code });
    case 'slant_not_usable':
      return t('quick.problems.slantNotUsable', { code: data.slant_code });
    case 'mixed_isolates':
      return t('quick.problems.mixedIsolates');
    case 'busy':
      return t('quick.problems.busy');
    default:
      return STORAGE_REFUSALS.includes(data?.code) ? storageErrorMessage(data, tSlants) : null;
  }
}

const wellInput = (v) => v.toUpperCase().replace(/\s+/g, '');

export default function DepositForm({ item, extra = {}, user, users = [], plates = [], slants = [], t, onDone, onCancel }) {
  // The slants catalogue first: storageErrorMessage says its refusals with
  // bare storage.* keys, which must resolve there and not in a catalogue
  // that happens to have keys of the same name. Everything else on the form
  // is said by the hub's own t (the prop).
  const { t: tSlants } = useTranslation(['slants', 'isolates', 'common']);
  // The drawer hands over the dossier's lists: its plates say has_slants.
  const sources = depositSources(item, { plates, slants });
  const offered = (id) => !!id && sources.plates.some((p) => p.plate_id === id);

  // Opened from a plate, the deposit cuts that plate's set; else it banks
  // the loose tubes when there are any, a new set when there are not.
  const [source, setSource] = useState(() => {
    if (offered(extra.plateId)) return 'plate';
    return sources.slants.length ? 'slants' : 'plate';
  });
  const [plateId, setPlateId] = useState(() => (offered(extra.plateId) ? extra.plateId
    : sources.plates.find((p) => p.status === 'ready_to_transfer')?.plate_id || defaultPlateId(item, sources.plates)));
  const [ticked, setTicked] = useState(() => new Set(sources.slants.map((s) => s.slant_id)));
  const [place, setPlace] = useState({ unit: '', unit_label: '', rack: '', box: '' });
  const [startWell, setStartWell] = useState('');
  // Wells typed for one tube, by tube: everything else follows the first well.
  const [ownWells, setOwnWells] = useState({});
  // What a culture being banked most likely says: preserved today, by the
  // person at the bench, as its own taxon -- as SlantStoragePanel suggests.
  const [preservation, setPreservation] = useState(() => ({
    preserved_at: localToday(), preserved_by: user?.user_id || '', preserved_organism: item?.taxon_name || '',
  }));
  // The tube whose well another tube already holds, marked on its well input.
  const [refused, setRefused] = useState(null);
  const { busy, error, setError, send } = useQuickSubmit('hub-deposit', {
    onError: (e) => {
      const data = e?.response?.data || {};
      const text = depositRefusal(data, t, tSlants);
      if (text) setError(text);
      setRefused(data.code === 'well_occupied' ? data.slant_code || null : null);
    },
  });

  const plate = sources.plates.find((p) => p.plate_id === plateId) || null;
  const chosen = source === 'plate' ? cutSetTubes(plate)
    : sources.slants.filter((s) => ticked.has(s.slant_id))
      .map((s) => ({ key: s.slant_id, slant_id: s.slant_id, code: s.slant_code, role: s.slant_role }));
  const filled = nextWells(startWell, chosen.length);
  const tubes = chosen.map((tube, i) => ({ ...tube, well: ownWells[tube.key] ?? filled[i] }));
  const form = { source, plate_id: plateId, ...place, ...preservation };

  // The people list holds active accounts; the person at the bench is named
  // even when it does not list them.
  const technicians = [...users];
  if (preservation.preserved_by && !technicians.some((u) => u.user_id === preservation.preserved_by)) {
    const me = preservation.preserved_by === user?.user_id;
    technicians.unshift({ user_id: preservation.preserved_by,
      username: (me && user?.username) || preservation.preserved_by });
  }

  const setPlaceField = (key) => (e) => setPlace((p) => ({ ...p, [key]: e.target.value }));
  const setPreserved = (key) => (e) => setPreservation((p) => ({ ...p, [key]: e.target.value }));
  const toggle = (id) => setTicked((was) => {
    const next = new Set(was);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const changeStartWell = (e) => {
    setStartWell(wellInput(e.target.value));
    setOwnWells({});
  };
  const changeTubeWell = (key) => (e) => {
    const value = wellInput(e.target.value);
    setOwnWells((w) => ({ ...w, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setRefused(null);
    const problem = depositProblem(form, tubes);
    if (problem) {
      setError(t(`quick.problems.${problem}`, { well: duplicateWell(tubes) }));
      return;
    }
    const res = await send('post', '/slants/deposit', depositBody(form, tubes));
    if (!res) return;
    const placed = (res.data?.slants || []).map((s) => s.slant_code).filter(Boolean);
    onDone(t('toast.deposited', { codes: (placed.length ? placed : tubes.map((tube) => tube.code)).join(', ') }));
  };

  if (!sources.plates.length && !sources.slants.length) return <p style={S.hint}>{t('quick.deposit.noSource')}</p>;

  return (
    <form onSubmit={submit} style={stack} data-testid="quick-deposit">
      <fieldset style={box}>
        <legend style={legend}>{t('quick.deposit.source')}</legend>
        <div style={stack}>
          <label style={choice}>
            <input type="radio" name="deposit-source" value="slants" checked={source === 'slants'}
              disabled={!sources.slants.length} onChange={() => setSource('slants')}
              title={t('quick.deposit.tipSlants')} data-testid="deposit-source-slants" />
            {t('quick.deposit.fromSlants')}
          </label>
          {source === 'slants' && (
            <ul style={list} data-testid="deposit-slants">
              {sources.slants.map((s) => (
                <li key={s.slant_id}>
                  <label style={choice}>
                    <input type="checkbox" checked={ticked.has(s.slant_id)} onChange={() => toggle(s.slant_id)}
                      title={t('quick.deposit.tipTube')} data-testid={`deposit-slant-${s.slant_id}`} />
                    <Code strong>{s.slant_code}</Code>
                    {s.slant_role && <span style={rolePill}>{t(`slants:slantRole.${s.slant_role}`)}</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <label style={choice}>
            <input type="radio" name="deposit-source" value="plate" checked={source === 'plate'}
              disabled={!sources.plates.length} onChange={() => setSource('plate')}
              title={t('quick.deposit.cutNote')} data-testid="deposit-source-plate" />
            {t('quick.deposit.fromPlate')}
          </label>
          {source === 'plate' && sources.plates.length > 0 && (
            <div style={{ ...stack, paddingInlineStart: 24 }}>
              <PlatePicker plates={sources.plates} value={plateId} onChange={setPlateId} t={t} />
              <p style={{ ...S.hint, margin: 0 }}>{t('quick.deposit.cutNote')}</p>
            </div>
          )}
        </div>
      </fieldset>

      <fieldset style={box}>
        <legend style={legend}>{t('quick.deposit.place')}</legend>
        <div style={grid}>
          <label style={S.label}>
            {t('slants:storage.unit')} *
            <select value={place.unit} onChange={setPlaceField('unit')} style={S.input} aria-required="true"
              title={t('slants:tip.storageUnit')} data-testid="deposit-unit">
              <option value="">{t('common:selectPlaceholder')}</option>
              {STORAGE_UNITS.map((u) => <option key={u} value={u}>{t(`specimens:storage.units.${u}`)}</option>)}
            </select>
          </label>
          <label style={S.label}>
            {t('slants:storage.freezerId')}
            <input value={place.unit_label} onChange={setPlaceField('unit_label')} maxLength={60} style={S.input}
              placeholder={t('slants:storage.freezerIdPlaceholder')} title={t('slants:tip.freezerId')}
              data-testid="deposit-freezer-id" />
          </label>
          <label style={S.label}>
            {t('slants:storage.rack')}
            <input value={place.rack} onChange={setPlaceField('rack')} maxLength={60} dir="ltr" style={S.input}
              title={t('slants:tip.rack')} data-testid="deposit-rack" />
          </label>
          <label style={S.label}>
            {t('slants:storage.box')}
            <input value={place.box} onChange={setPlaceField('box')} maxLength={60} dir="ltr" style={S.input}
              title={t('slants:tip.box')} data-testid="deposit-box" />
          </label>
          <label style={S.label}>
            {t('quick.deposit.startWell')}
            {/* "c 7" is C7 to the server too; written so here as it is typed. */}
            <input value={startWell} onChange={changeStartWell} maxLength={5} dir="ltr" autoCapitalize="characters"
              autoComplete="off" spellCheck={false} style={{ ...S.input, ...S.mono }}
              title={t('quick.deposit.startWellHint')} data-testid="deposit-start-well" />
          </label>
        </div>
        <p style={{ ...S.hint, margin: '8px 0 0' }}>{t('quick.deposit.startWellHint')}</p>
      </fieldset>

      {tubes.length > 0 && (
        <fieldset style={box}>
          <legend style={legend}>{t('quick.deposit.tubes')}</legend>
          <ol style={list} data-testid="deposit-tubes">
            {tubes.map((tube, i) => (
              <li key={tube.key} style={tubeRow}>
                <Code strong>{tube.code}</Code>
                {tube.role && <span style={rolePill}>{t(`slants:slantRole.${tube.role}`)}</span>}
                <input value={tube.well} onChange={changeTubeWell(tube.key)} maxLength={5} dir="ltr"
                  autoCapitalize="characters" autoComplete="off" spellCheck={false}
                  aria-label={t('quick.deposit.tubeWell', { code: tube.code })}
                  aria-invalid={refused === tube.code ? 'true' : undefined}
                  style={{ ...S.input, ...S.mono, width: 90, marginInlineStart: 'auto' }}
                  title={t('slants:tip.well')} data-testid={`deposit-well-${i}`} />
              </li>
            ))}
          </ol>
        </fieldset>
      )}

      <fieldset style={box}>
        <legend style={legend}>{t('quick.deposit.preservation')}</legend>
        <div style={grid}>
          <label style={S.label}>
            {t('slants:storage.preservedAt')} *
            <input type="date" value={preservation.preserved_at} onChange={setPreserved('preserved_at')}
              max={localToday()} aria-required="true" style={S.input} title={t('slants:tip.preservedAt')}
              data-testid="deposit-preserved-at" />
          </label>
          <label style={S.label}>
            {t('slants:storage.preservedBy')} *
            <select value={preservation.preserved_by} onChange={setPreserved('preserved_by')} style={S.input}
              aria-required="true" title={t('slants:tip.preservedBy')} data-testid="deposit-preserved-by">
              <option value="">{t('slants:selectUserPlaceholder')}</option>
              {technicians.map((u) => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
            </select>
          </label>
          <label style={{ ...S.label, gridColumn: '1 / -1' }}>
            {t('slants:storage.organism')} *
            <input value={preservation.preserved_organism} onChange={setPreserved('preserved_organism')}
              maxLength={300} dir="ltr" aria-required="true" style={S.input} title={t('slants:tip.organism')}
              data-testid="deposit-organism" />
          </label>
        </div>
      </fieldset>

      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.deposit.save')}
        saveTip={t('tip.action.deposit')} testId="deposit-save" />
    </form>
  );
}

const box = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, margin: 0, minWidth: 0 };
const legend = { fontSize: 13, fontWeight: 600, padding: '0 4px' };
const choice = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14, cursor: 'pointer' };
const list = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 };
const tubeRow = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const rolePill = {
  display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  background: 'var(--c-bg-muted)', color: 'var(--c-text-muted-fg)', border: '1px solid var(--c-border)',
};
